import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from "recharts";

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="chart-tooltip">
        <p className="tooltip-title">{label}</p>
        {payload.map((item, index) => (
          <p key={index} className="tooltip-item" style={{ color: item.color }}>
            {item.name}: {item.value?.toLocaleString()}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

function MonthlyChart(props) {
  const { data } = props;

  if (!data || data.length === 0) {
    return (
      <div className="chart-empty-state">
        <p>No monthly data available</p>
      </div>
    );
  }

  return (
    <div className="monthly-chart-container">
      <div className="chart-header">
        <h3>Monthly Performance Overview</h3>
        <p className="chart-subtitle">Sales, Tax, Discount & Net Amount Trends</p>
      </div>
      <div className="chart-wrapper">
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={data}
            margin={{
              top: 20,
              right: 30,
              left: 20,
              bottom: 20,
            }}
          >
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e0e0e0"
              vertical={false}
            />
            <XAxis 
              dataKey="monthname" 
              tick={{ fontSize: 12, fill: '#666' }}
              axisLine={{ stroke: '#ccc' }}
              tickLine={{ stroke: '#ccc' }}
            />
            <YAxis 
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={(value) => `${value?.toLocaleString()}`}
              width={80}
              axisLine={{ stroke: '#ccc' }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend 
              wrapperStyle={{ paddingTop: 15 }}
              iconType="circle"
              formatter={(value) => <span style={{ color: '#333', fontSize: 12 }}>{value}</span>}
            />
            <Line
              type="monotone"
              dataKey="Sale"
              stroke="#8884d8"
              strokeWidth={3}
              dot={{ r: 5, fill: "#8884d8", strokeWidth: 2 }}
              activeDot={{ r: 8 }}
              name="Total Sale"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="SalesTax" 
              stroke="#ffc658"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#ffc658" }}
              name="Sales Tax"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="Discount" 
              stroke="#ff8042"
              strokeWidth={2.5}
              dot={{ r: 4, fill: "#ff8042" }}
              name="Discount"
              animationDuration={1500}
            />
            <Line 
              type="monotone" 
              dataKey="NetAmount" 
              stroke="#82ca9d"
              strokeWidth={3}
              dot={{ r: 5, fill: "#82ca9d", strokeWidth: 2 }}
              name="Net Amount"
              animationDuration={1500}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="chart-footer">
        <div className="legend-info">
          <span className="legend-dot sale"></span>
          <span>Total Sale</span>
          <span className="legend-dot tax"></span>
          <span>Sales Tax</span>
          <span className="legend-dot discount"></span>
          <span>Discount</span>
          <span className="legend-dot net"></span>
          <span>Net Amount</span>
        </div>
      </div>

      <style >{`
        .monthly-chart-container {
          background: white;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }
        
        .monthly-chart-container:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.12);
        }
        
        .chart-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #f0f0f0;
        }
        
        .chart-header h3 {
          margin: 0 0 5px 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
        
        .chart-subtitle {
          margin: 0;
          font-size: 12px;
          color: #999;
        }
        
        .chart-wrapper {
          width: 100%;
          overflow-x: auto;
        }
        
        .chart-footer {
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #f0f0f0;
        }
        
        .legend-info {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 20px;
          align-items: center;
          font-size: 12px;
          color: #666;
        }
        
        .legend-dot {
          display: inline-block;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          margin-right: 5px;
        }
        
        .legend-dot.sale {
          background: #8884d8;
        }
        
        .legend-dot.tax {
          background: #ffc658;
        }
        
        .legend-dot.discount {
          background: #ff8042;
        }
        
        .legend-dot.net {
          background: #82ca9d;
        }
        
        .chart-empty-state {
          background: white;
          border-radius: 16px;
          padding: 60px 20px;
          text-align: center;
          color: #999;
          font-size: 14px;
        }
        
        /* Global Tooltip Styles */
        :global(.chart-tooltip) {
          background: rgba(0, 0, 0, 0.85);
          padding: 12px 16px;
          border-radius: 8px;
          color: white;
          min-width: 180px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        
        :global(.tooltip-title) {
          margin: 0 0 8px 0;
          font-size: 13px;
          font-weight: 600;
          color: #ffd700;
          border-bottom: 1px solid rgba(255,255,255,0.2);
          padding-bottom: 5px;
        }
        
        :global(.tooltip-item) {
          margin: 5px 0;
          font-size: 12px;
          font-weight: 500;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .monthly-chart-container {
            padding: 15px;
          }
          
          .chart-header h3 {
            font-size: 14px;
          }
          
          .legend-info {
            gap: 12px;
            font-size: 10px;
          }
          
          :global(.recharts-default-legend) {
            display: flex !important;
            flex-wrap: wrap !important;
            justify-content: center !important;
            gap: 10px !important;
          }
        }
        
        @media (max-width: 480px) {
          .legend-info {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }
        }
      `}</style>
    </div>
  );
}

export default MonthlyChart;